import { type ReactNode } from 'react';
import { HostedSidebar } from '../components/hosted/sidebar';
import HostedFooter from '../components/hosted/Footer';
import HostedDemoBanner from '../components/hosted/DemoBanner';
import HostedLanguageSelector from '../components/hosted/LanguageSelector';
import HostedScrollArea from '../components/hosted/ScrollArea';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { useLayoutData } from '../hooks/useLayoutContext';

type DesktopLayoutProps = {
  children: ReactNode;
};

/**
 * HostedDesktopLayout — Desktop page shell for Ronan v2.
 *
 * Reads sidebar/layout data from LayoutContext.
 * Structure: persistent sidebar (left) + scrollable main content (right).
 */
export default function HostedDesktopLayout({ children }: DesktopLayoutProps) {
  const {
    client,
    requestType,
    interactionConfig,
    requestedDataGroups,
    requestedDataLoading,
    demo,
  } = useLayoutData();

  return (
    <div className="flex flex-col h-dvh w-full font-inter">
      {demo && <HostedDemoBanner />}
      <div className="flex flex-1 min-h-0 w-full">
        <aside className="flex flex-col bg-sidebar w-lg shrink-0">
          <HostedScrollArea className="pl-10 pr-12">
            <HostedSidebar
              client={client}
              requestType={requestType}
              interactionConfig={interactionConfig}
              requestedDataGroups={requestedDataGroups}
              requestedDataLoading={requestedDataLoading}
            />
          </HostedScrollArea>
        </aside>

        <div className="flex flex-1 flex-col min-w-0 bg-background relative pt-5">
          <div className="absolute right-5 top-5">
            <HostedLanguageSelector />
          </div>

          <main className="flex flex-1 flex-col items-center overflow-y-auto px-10 pb-10">
            <ErrorBoundary>{children}</ErrorBoundary>
          </main>

          <HostedFooter />
        </div>
      </div>
    </div>
  );
}
